import React, { useState, useEffect, useRef } from 'react';
import { Modal } from 'antd';
import { postAddCart, getTogetherProducts, calculateTogetherProductsPrices } from '@base/product/services';
import { useStore } from 'base/store';
import { displayCtr, trackAddToCart } from 'base/utils/tracker';
import Message from '@base/lib/Message';
import { ITrackVariant, ITogether, ITogetherPrice, ITogetherProduct } from 'base/interfaces/product';
import styles from '@app/product/ProductTogetherProducts/index.module.less';
import { isSoldOut } from 'base/utils/product';
import { useTrans } from 'base/utils/i18n';

type TTogetherMap = Record<string, ITogether>;

export type ITogetherInstance = {
    wrapperRef: React.RefObject<HTMLDivElement>;
    list: ITogetherProduct[];
    selected: string[];
    setSelected: (ids: string[]) => void;
    loading: boolean;
    calculateLoading: boolean;
    addToCart: () => Promise<void>;
    priceData?: ITogetherPrice;
};

type IUseTogetherParams = {
    productId: string;
    variantId: string;
    moduleId: string;
}

const getCommerceData = (products: ITogetherProduct[], selectedIds: string[]): ITrackVariant[] => {
    const arr: ITrackVariant[] = [];
    products.forEach(item => {
        if (selectedIds.includes(item.variantId)) {
            arr.push({
                ...item,
                num: 1,
            });
        }
    });
    return arr;
};

function getTogetherMap(list: ITogether[]): TTogetherMap | null {
    if (list.length === 0) {
        return null;
    }
    const o: TTogetherMap = {};
    list.forEach(item => {
        o[item.productVariantId] = item;
    })
    return o;
}

function getVariantList(variantId: string, tmap: TTogetherMap): ITogetherProduct[] {
    let list = tmap[variantId]?.variantTogetherList;
    if (!list?.length) {
        list = tmap['0']?.variantTogetherList;
    }
    // 在售、库存
    list = list?.filter((item) => {
        return item.isOnSale && item.isStock
    })
    if (!list?.length) {
        return [];
    }
    return list.length > 3 ? list.splice(0, 3) : list;
}

const getCanAddToCartList = (list: ITogetherProduct[]): ITogetherProduct[] => {
    return list.filter(item => !isSoldOut(item))
}

const getSelected = (list: ITogetherProduct[]): string[] => {
    return list.map(item => item.variantId);
};

export default function useTogether(params: IUseTogetherParams): ITogetherInstance {
    const { productId, variantId, moduleId } = params;
    const { miniCartStore } = useStore();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const togetherMap = useRef<TTogetherMap | null>(null);
    const [list, setList] = useState<ITogetherProduct[]>([]);
    const [selected, setSelected] = useState<string[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [calculateLoading, setCalculateLoading] = useState<boolean>(false);
    const [priceData, setPriceData] = useState<ITogetherPrice>();

    const { t } = useTrans();

    const timerRef = useRef<any>();

    // 计算buy together总价
    const calculateBuyTogetherPrice = async () => {
        setCalculateLoading(true);
        try {
            const res = await calculateTogetherProductsPrices({
                variantId: variantId,
                togetherVariantIds: selected,
            })

            setPriceData(res)
        } catch (err) {

        } finally {
            setCalculateLoading(false);
        }
    }

    const reRender = () => {
        const tmap = togetherMap.current;
        if (!tmap) {
            setList([]);
            setSelected([]);
        } else {
            const _list = getVariantList(variantId, tmap);
            setList(_list);
            const _canAddToCartList = getCanAddToCartList(_list)
            setSelected(getSelected(_canAddToCartList));
        }
    };

    // 添加购物车
    const addToCart = async () => {
        if (selected.length === 0) {
            Message.error(t('web_product_together_empty_error'));
            return;
        }
        setLoading(true);
        trackAddToCart(getCommerceData(list, selected));
        try {

            // 主商品
            const mainProduct = {
                productVariantId: variantId,
                num: 1,
                mainVariant: true,
            }
            // 搭配商品
            const togetherProducts = selected.map((id) => ({
                productVariantId: id,
                num: 1
            }))

            const res = await postAddCart(
                [
                    mainProduct,
                    ...togetherProducts
                ],
                'buyTogether'
            );

            const totalLen = selected.length + 1

            if (!res.addFailProductName || res.addFailProductName.length === 0) {
                let srcList: string[] = [];
                if (wrapperRef && wrapperRef.current) {
                    const imageList = wrapperRef.current.querySelectorAll(`.${styles.checked} .picture img`);
                    srcList = Array.from(imageList).map(image => (image as HTMLImageElement).currentSrc);
                }
                miniCartStore.afterAddCart(totalLen, srcList);
            } else {
                if (res.addFailProductName.length < totalLen) {
                    Modal.error({
                        title: (
                            <div className="text-left">
                                {t(res.addFailProductName.length == 1 ? "web_product_together_item_unavailable" : "web_product_together_items_unavailable")}:
                            </div>
                        ),
                        content: (
                            <div className="text-left">
                                {res.addFailProductName.map((item, index) => (
                                    <p key={index} className="my-1">{item}</p>
                                ))}
                            </div>
                        ),
                    });
                    miniCartStore.setTotalQty(miniCartStore.state.totalQty + (totalLen - res.addFailProductName.length));
                    miniCartStore.reload();
                } else {
                    Message.error(t('web_product_together_all_unavailable'));
                }
            }
        } catch (err) {
            Message.error(err.message);
        }
        setLoading(false);
    };


    useEffect(() => {
        togetherMap.current = null;
        reRender();
        getTogetherProducts({ productId, variantId }).then(res => {
            // TODO: wholesale remove
            // res.forEach(together => {
            //     together.variantTogetherList.forEach(item => item.dealerShopPriceExchangeDisplay = '$5.00')
            // })
            togetherMap.current = getTogetherMap(res);
            reRender();
        });
    }, [productId]);

    useEffect(() => {
        if (togetherMap.current) {
            reRender();
        }
    }, [variantId]);

    useEffect(() => {
        timerRef?.current && clearTimeout(timerRef.current)
        timerRef.current = setTimeout(()=> {
            calculateBuyTogetherPrice().then()
        }, 1000)
    }, [selected]);

    useEffect(() => {
        if (list.length > 0) {
            displayCtr(moduleId)
        }
    }, [list]);

    return {
        wrapperRef,
        list,
        selected,
        setSelected,
        loading,
        calculateLoading,
        addToCart,
        priceData,
    }
}